/**
 * BIG MOMENTS PACE — D-EZ-103.
 *
 * A pace beside Slow / Race pace / Quick. Quiet turns go by quickly with no dice
 * motion; the race stops to roll on the table (full dice) only for the turns a
 * fan would want to see rolled:
 *
 *   • the green flag and every restart,
 *   • pit stops,
 *   • the last five scheduled turns,
 *   • a lead fight in the TOP group, and trouble (a caution or a wreck).
 *
 * ===========================================================================
 * IT NEVER LOOKS AHEAD. This is the whole design.
 * ===========================================================================
 *
 * The race is already resolved, so it would be trivially easy — and forbidden
 * (AGENTS.md: "Never choose invitations by looking at an unseen winner, wreck,
 * retirement, or queued result") — to read the next turn and stop because a
 * wreck is coming. Two gates instead, and neither can see an unrevealed die:
 *
 *   GATE 1 — BEFORE THE TURN, from what is on screen: the clock (turn 1, the last
 *   five), the pit schedule the timing tower already prints ("turn 24 · pit
 *   stop"), and the flag (a caution that has just run out means the next turn is
 *   a restart). `momentBeforeTurn` takes ONLY those visible numbers.
 *
 *   GATE 2 — THE RACE CAM CALL. Every green turn begins with the race cam roll
 *   (black die = which group, metallic pair = what the track card says). Big
 *   moments REVEALS that call first — it is put on screen as "Race cam · turn N:
 *   TOP group — HEAT" — and only then decides, from the call alone
 *   (`momentFromCall`), whether to roll the rest of the turn on the table or let
 *   the cars move. A lead fight is the TOP group (or a duel) on a quality; trouble
 *   is a TROUBLE, PROBLEM or duel call. Nothing after the race cam is read.
 *
 * `planMomentStep` is the only decision, and its input type has no field that
 * could carry a future turn: the visible state, and the call already revealed.
 * `bigMoments.test.ts` pins that by doctoring the future of a real race and
 * asserting every decision is unchanged.
 *
 * WHAT IT CANNOT CATCH, stated: a caution that comes out of a quiet race cam
 * call (a house-rule loose car on a quality turn, D-EZ-83) moves the cars
 * without dice. The very next turns — the caution laps' end and the restart —
 * are caught by gate 1.
 */

import type { RaceCamRead, TurnKind, TurnResult } from '../domain/turn.ts';
import { RAIN_OFFICIAL_FRACTION } from '../domain/turn.ts';
import { QUALITIES, type RaceCamResult } from '../domain/types.ts';

// ---------------------------------------------------------------------------
// The pace dial
// ---------------------------------------------------------------------------

export type PaceKey = 'commissioner' | 'moments' | 'slow' | 'race' | 'quick';

export interface PaceOption {
  key: PaceKey;
  label: string;
  /** Milliseconds between quiet turns. */
  ms: number;
}

export const PACE_OPTIONS: readonly PaceOption[] = [
  // D-EZ-105 — quiet turns run themselves; the table stops for the pivotal throws and waits for you.
  { key: 'commissioner', label: 'You roll the big ones', ms: 260 },
  { key: 'moments', label: 'Big moments', ms: 320 },
  { key: 'slow', label: 'Slow', ms: 1900 },
  { key: 'race', label: 'Race pace', ms: 1000 },
  { key: 'quick', label: 'Quick', ms: 420 },
];

/** How long a revealed race cam call holds before the turn is decided. */
export const PEEK_HOLD_MS = 520;
/** How long the board holds after a big moment has rolled and the cars moved. */
export const AFTER_BIG_HOLD_MS = 1100;

export function paceMs(key: PaceKey): number {
  return PACE_OPTIONS.find((p) => p.key === key)?.ms ?? 1000;
}

/**
 * v2 since D-EZ-105 made "You roll the big ones" the season default. A v1
 * choice of anything but the old default (Big moments) is still honoured.
 */
export const PACE_PREF = 'rwbr.pace.v2';
export const PACE_PREF_V1 = 'rwbr.pace.v1';

const isPace = (v: unknown): v is PaceKey => PACE_OPTIONS.some((p) => p.key === v);

/** The pace this browser last chose, or null if it never chose one. */
export function readPacePref(storage: Pick<Storage, 'getItem'> | null = safeStorage()): PaceKey | null {
  try {
    const raw = storage?.getItem(PACE_PREF);
    if (isPace(raw)) return raw;
    const old = storage?.getItem(PACE_PREF_V1);
    return isPace(old) && old !== 'moments' ? old : null;
  } catch {
    return null;
  }
}

export function savePacePref(key: PaceKey, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(PACE_PREF, key);
  } catch {
    /* the race still plays at this pace for this page */
  }
}

/** A remembered choice wins; otherwise season races open on "You roll the big ones" and an exhibition on Race pace. */
export function defaultPace(stored: PaceKey | null, seasonRace: boolean): PaceKey {
  return stored ?? (seasonRace ? 'commissioner' : 'race');
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Gate 1 — before the turn, from what is on screen
// ---------------------------------------------------------------------------

export type MomentReason = 'green-flag' | 'restart' | 'pit-stop' | 'closing-laps' | 'lead-fight' | 'trouble' | 'rain' | 'battle';

export const MOMENT_WORDS: Record<MomentReason, string> = {
  'green-flag': 'The green flag',
  restart: 'The restart',
  'pit-stop': 'Pit stops',
  'closing-laps': 'The closing laps',
  'lead-fight': 'A fight for the lead',
  trouble: 'Trouble on track',
  rain: 'Rain on the way',
  battle: 'A run at the top seven',
};

/** Exactly what the timing tower and the flag stand already show before turn N. */
export interface VisibleTurnState {
  /** The turn about to be run (`RaceState.turn` after the last one shown). */
  nextTurn: number;
  scheduledTurns: number;
  /** The pit schedule as it stands on screen. */
  pitTurns: readonly number[];
  /** Caution turns still to run, as of the last turn shown. */
  yellowTurnsRemaining: number;
  /** Was the last turn shown run under the yellow? False before the green. */
  lastTurnUnderYellow: boolean;
  /** A RAIN THREAT on screen: the turn from whose end the rain die is rolled. */
  rainCheckFrom?: number;
}

/** Gate 1. Only the visible clock, pit schedule and flag. */
export function momentBeforeTurn(v: VisibleTurnState): MomentReason | null {
  if (v.nextTurn <= 1) return 'green-flag';
  if (v.pitTurns.includes(v.nextTurn)) return 'pit-stop';
  if (v.nextTurn > v.scheduledTurns - 5) return 'closing-laps';
  if (v.lastTurnUnderYellow && v.yellowTurnsRemaining === 0) return 'restart';
  return null;
}

/** Build the visible state from what the board is showing. */
export function visibleTurnState(shown: {
  state: { turn: number; pitTurns: readonly number[]; yellowTurnsRemaining: number; rainCheckFrom?: number };
  previous: { yellowTurnsRemaining: number } | null;
  turn: { kind: TurnKind } | null;
}, scheduledTurns: number): VisibleTurnState {
  return {
    nextTurn: shown.state.turn,
    scheduledTurns,
    pitTurns: shown.state.pitTurns,
    yellowTurnsRemaining: shown.state.yellowTurnsRemaining,
    lastTurnUnderYellow:
      shown.turn !== null && (shown.turn.kind === 'yellow' || (shown.previous?.yellowTurnsRemaining ?? 0) > 0),
    ...(shown.state.rainCheckFrom !== undefined ? { rainCheckFrom: shown.state.rainCheckFrom } : {}),
  };
}

// ---------------------------------------------------------------------------
// Gate 2 — the race cam call, once it is on screen
// ---------------------------------------------------------------------------

export interface CamCall {
  /** The turn this call opens. */
  turn: number;
  group: RaceCamRead['group'];
  result: RaceCamResult;
  /** "TOP group — HEAT", for the screen. */
  line: string;
}

/** What the screen shows once the race cam has been revealed for a turn (null: no call that turn). */
export interface Peek {
  turn: number;
  call: CamCall | null;
}

const GROUP_WORD: Record<RaceCamRead['group'], string> = {
  top: 'TOP group',
  middle: 'MIDDLE group',
  bottom: 'BOTTOM group',
  duel: 'a DUEL',
};

/**
 * REVEAL the race cam call of a turn. Call this only to put the call on screen —
 * it is the first roll of the turn and it is shown before anything else is.
 */
export function revealCamCall(turn: TurnResult): Peek {
  const cam = turn.raceCam;
  if (!cam || turn.kind !== 'green') return { turn: turn.turn, call: null };
  return {
    turn: turn.turn,
    call: {
      turn: turn.turn,
      group: cam.group,
      result: cam.result,
      // A duel is its own card: the black die called it, and the quality read is not what happens.
      line: cam.group === 'duel' ? 'a DUEL — somebody is in trouble' : `${GROUP_WORD[cam.group]} — ${cam.result}`,
    },
  };
}

const isQuality = (r: RaceCamResult) => (QUALITIES as readonly string[]).includes(r);

/** Gate 2. Only the revealed call. */
export function momentFromCall(call: CamCall | null): MomentReason | null {
  if (!call) return null;
  if (call.result === 'TROUBLE' || call.result === 'PROBLEM' || call.group === 'duel') return 'trouble';
  if (call.group === 'top' && isQuality(call.result)) return 'lead-fight';
  return null;
}

// ---------------------------------------------------------------------------
// The one decision
// ---------------------------------------------------------------------------

export type MomentStep =
  /** Roll turn `target` on the table, full dice, then move the cars. */
  | { kind: 'table'; target: number; reason: MomentReason }
  /** Put turn `target`'s race cam call on screen first. No dice motion. */
  | { kind: 'peek'; target: number }
  /** A quiet turn: move the cars with no dice motion. */
  | { kind: 'reveal'; target: number };

/**
 * What Big moments does next. `visible` is what is on screen; `peek` is the race
 * cam call already revealed (or null). There is no third input.
 */
export function planMomentStep(visible: VisibleTurnState, peek: Peek | null): MomentStep {
  const target = visible.nextTurn;
  const before = momentBeforeTurn(visible);
  if (before) return { kind: 'table', target, reason: before };
  // A caution lap is the field following the pace car: nothing to call.
  if (visible.yellowTurnsRemaining > 0) return { kind: 'reveal', target };
  if (!peek || peek.turn !== target) return { kind: 'peek', target };
  const fromCall = momentFromCall(peek.call);
  return fromCall ? { kind: 'table', target, reason: fromCall } : { kind: 'reveal', target };
}

// ---------------------------------------------------------------------------
// D-EZ-105 — THE COMMISSIONER ROLLS THE BIG MOMENTS
// ---------------------------------------------------------------------------


/**
 * Which turns the commissioner's table even opens for. The same two gates as
 * Big moments and nothing else: what is on screen before the turn, then the
 * race cam call once it has been revealed. Which THROW inside the turn stops
 * for him is `presentation/stakes/stakes.ts` `planTurn`, decided throw by throw
 * from what the table has already shown.
 *
 *   • the final turn (the last lead fights and the photo finish),
 *   • every pit stop (each ends with a shot at the lead off pit road),
 *   • a rain threat on screen, past halfway (the race could end on the die),
 *   • a TOP-group quality call (a lead fight), a MIDDLE-group quality call
 *     (runs at the top seven, two beats), any RARE result, and TROUBLE
 *     in the TOP or MIDDLE group (twelve cars: three or more are likely).
 */
export function planCommissionerStep(v: VisibleTurnState, peek: Peek | null): MomentStep {
  const target = v.nextTurn;
  if (target >= v.scheduledTurns) return { kind: 'table', target, reason: 'closing-laps' };
  // Every pit stop ends with a shot at the lead off pit road.
  if (v.pitTurns.includes(target)) return { kind: 'table', target, reason: 'pit-stop' };
  if (v.rainCheckFrom !== undefined && target >= v.rainCheckFrom && target >= v.scheduledTurns * RAIN_OFFICIAL_FRACTION) {
    return { kind: 'table', target, reason: 'rain' };
  }
  if (v.yellowTurnsRemaining > 0) return { kind: 'reveal', target };
  if (!peek || peek.turn !== target) return { kind: 'peek', target };
  const call = peek.call;
  if (!call || call.group === 'duel') return { kind: 'reveal', target };
  if (call.group === 'top' && isQuality(call.result)) return { kind: 'table', target, reason: 'lead-fight' };
  // A MIDDLE-group quality call sends cars at the top seven (the white die picks the man).
  if (call.group === 'middle' && isQuality(call.result)) return { kind: 'table', target, reason: 'battle' };
  if (call.result === 'RARE') return { kind: 'table', target, reason: 'trouble' };
  if (call.result === 'TROUBLE' && (call.group === 'top' || call.group === 'middle')) {
    return { kind: 'table', target, reason: 'trouble' };
  }
  return { kind: 'reveal', target };
}
